/**
 * The two things a user can do about a blocked write, shared verbatim between
 * apps/mobile/src/lib/offline/ and apps/web/src/lib/offline/.
 * Kept byte-identical by offline-queue.parity.test.ts.
 *
 * These exist because TanStack only persists *paused* mutations. A write that
 * actually failed — a 500, or a request whose reply was lost — is no longer
 * paused, so it disappears from the mutation cache and nothing would ever run
 * it again. The ledger keeps the full envelope precisely so these two
 * functions can rebuild the mutation from scratch, including after a restart.
 */
import type { QueryClient } from '@tanstack/react-query'

import type {
  OfflineLedger,
  OfflineLedgerEntry,
  OfflineMutationEnvelope,
} from './offline-queue'
import { OFFLINE_MUTATION_KEYS } from './offline-queue'

function findPausedMutations(
  queryClient: QueryClient,
  mutationKey: readonly string[],
  idempotencyKey: string,
) {
  return queryClient
    .getMutationCache()
    .findAll({ mutationKey: [...mutationKey] })
    .filter(mutation => {
      const variables = mutation.state.variables as
        | OfflineMutationEnvelope
        | undefined
      return variables?.idempotencyKey === idempotencyKey
    })
}

/**
 * Send a blocked write again, on the user's explicit say-so.
 *
 * Resetting the row to `queued` is what makes this different from an automatic
 * replay: it clears the `needs-review` / `inflight` guard that deliberately
 * refuses to re-send an at-most-once write on its own. The user has looked at
 * the item and decided the duplicate risk is acceptable — that decision is
 * theirs to make, and only theirs.
 */
export async function retryOfflineWrite(
  queryClient: QueryClient,
  ledger: OfflineLedger,
  entry: OfflineLedgerEntry,
): Promise<void> {
  const { envelope } = entry
  const mutationKey = OFFLINE_MUTATION_KEYS[envelope.kind]

  ledger.patch(envelope.idempotencyKey, {
    status: 'queued',
    attempts: 0,
    message: undefined,
  })

  // Drop any stale paused copy first, so a later `resumePausedMutations`
  // cannot run this envelope a second time behind the user's back.
  findPausedMutations(
    queryClient,
    mutationKey,
    envelope.idempotencyKey,
  ).forEach(mutation => {
    queryClient.getMutationCache().remove(mutation)
  })

  const defaults = queryClient.getMutationDefaults([...mutationKey])
  if (!defaults.mutationFn) {
    ledger.patch(envelope.idempotencyKey, {
      status: 'failed',
      message:
        'This change can no longer be sent from this version of the app.',
    })
    return
  }

  const mutation = queryClient.getMutationCache().build(queryClient, {
    ...defaults,
    mutationKey: [...mutationKey],
  })

  try {
    await mutation.execute(envelope)
  } catch {
    // The ledger row already carries the outcome and the sheet re-renders from
    // it; rethrowing here would only produce an unhandled rejection.
  }
}

/**
 * Throw the write away for good.
 *
 * Removing the paused mutation matters as much as removing the ledger row —
 * leaving it behind would let the next reconnect resurrect something the user
 * just told us to forget.
 */
export function discardOfflineWrite(
  queryClient: QueryClient,
  ledger: OfflineLedger,
  entry: OfflineLedgerEntry,
): void {
  const { envelope } = entry
  const mutationKey = OFFLINE_MUTATION_KEYS[envelope.kind]

  findPausedMutations(
    queryClient,
    mutationKey,
    envelope.idempotencyKey,
  ).forEach(mutation => {
    queryClient.getMutationCache().remove(mutation)
  })

  ledger.remove(envelope.idempotencyKey)
}

/** Human label for a ledger row's status, shared by both pending-writes UIs. */
export function describeOfflineWriteStatus(entry: OfflineLedgerEntry): string {
  switch (entry.status) {
    case 'queued':
      return 'Waiting to send'
    case 'inflight':
      return 'Sending'
    case 'succeeded':
      return 'Sent'
    case 'needs-review':
      return entry.message ?? 'Check before resending'
    case 'expired':
      return 'Too old to send safely'
    case 'failed':
    default:
      return entry.message ?? 'Could not be saved'
  }
}
